import { memo } from 'react';
import { TextField } from 'react-aria-components';

import { BasicTooltip, CopyText, Flex, Heading, Input, Label, Separator, Text, TextArea } from '@/components/core';
import { findField, FormattersSection, NewFieldHelpProps, ValidatorsSection } from '@/components/ui';
import { useModelConfigStore } from '@/store';
import { toString } from '@/utils';

export type FieldDetailsProps = {
  fieldId: string;
  newFieldHelp?: NewFieldHelpProps;
};

const IFieldDetails = ({ fieldId, newFieldHelp }: FieldDetailsProps) => {
  const updateField = useModelConfigStore((state) => state.updateField);
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));

  const isTableField = field?.type === 'table';

  if (!field) {
    return null;
  }

  return (
    <>
      <Flex col className="p-md gap-md group/field-details">
        <TextField
          className="flex flex-col gap-1"
          defaultValue={newFieldHelp ? newFieldHelp.defaultName : field.name}
          onChange={(name) => updateField(field.id, { name }, field.parentId)}
        >
          <Label isRequired>Field name</Label>
          <Input placeholder={newFieldHelp?.namePlaceholder || 'My new field'} />
        </TextField>
        <TextField
          className="flex flex-col gap-1"
          defaultValue={toString(field.promptHint)}
          onChange={(promptHint) => updateField(field.id, { promptHint }, field.parentId)}
        >
          <Label isRequired>Prompt hint</Label>
          <TextArea placeholder={newFieldHelp?.promptHintPlaceholder} />
        </TextField>

        {!isTableField && (
          <>
            <section className="gap-xs flex flex-col">
              <Flex className="gap-sm items-center">
                <Heading size="xs">Field ID</Heading>
                <BasicTooltip>
                  <Text>The unique ID of this field. This property cannot be changed.</Text>
                </BasicTooltip>
              </Flex>

              <CopyText value={fieldId} />
            </section>
            <Separator orientation="horizontal" />
            <FormattersSection fieldId={fieldId} />
            <ValidatorsSection fieldId={fieldId} />
          </>
        )}
      </Flex>
    </>
  );
};

export const FieldDetails = memo(IFieldDetails) as typeof IFieldDetails;
